import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const IATA = z.string().regex(/^[A-Z]{3}$/);

export type PriceHistoryPoint = {
  checkedAt: string; // ISO
  displayPrice: number;
  realMinPrice: number;
};

export type PriceHistoryResult = {
  points: PriceHistoryPoint[];
  min: number | null;
  max: number | null;
  current: number | null;
  currency: string;
};

/**
 * Returns price history snapshots for a route over the last N days.
 * Downsamples by keeping the lowest display_price per day so the sparkline
 * reflects the "best deal" trend (which is what users actually care about).
 */
export const getPriceHistory = createServerFn({ method: "GET" })
  .inputValidator((data: { from: string; to: string; days?: number }) =>
    z
      .object({
        from: IATA,
        to: IATA,
        days: z.number().int().min(1).max(180).optional(),
      })
      .parse(data),
  )
  .handler(async ({ data }): Promise<PriceHistoryResult> => {
    const days = data.days ?? 29;
    const since = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();

    const supabaseUrl = process.env.SUPABASE_URL || process.env.VITE_SUPABASE_URL;
    const anonKey = process.env.SUPABASE_PUBLISHABLE_KEY || process.env.SUPABASE_ANON_KEY;
    if (!supabaseUrl || !anonKey) {
      return { points: [], min: null, max: null, current: null, currency: "PLN" };
    }

    const url =
      `${supabaseUrl}/rest/v1/route_price_history` +
      `?from_iata=eq.${data.from}&to_iata=eq.${data.to}` +
      `&checked_at=gte.${encodeURIComponent(since)}` +
      `&select=checked_at,real_min_price,display_price,currency` +
      `&order=checked_at.asc&limit=1000`;

    const res = await fetch(url, {
      headers: { apikey: anonKey, Authorization: `Bearer ${anonKey}` },
    });
    if (!res.ok) {
      return { points: [], min: null, max: null, current: null, currency: "PLN" };
    }
    const rows = (await res.json()) as Array<{
      checked_at: string;
      real_min_price: number;
      display_price: number;
      currency: string;
    }>;
    if (rows.length === 0) {
      return { points: [], min: null, max: null, current: null, currency: "PLN" };
    }

    // Downsample: keep min display_price per day for cleaner sparkline
    const byDay = new Map<string, { row: typeof rows[number]; latestTs: number }>();
    for (const r of rows) {
      const day = r.checked_at.slice(0, 10);
      const existing = byDay.get(day);
      const ts = new Date(r.checked_at).getTime();
      if (!existing || r.display_price < existing.row.display_price) {
        byDay.set(day, { row: r, latestTs: ts });
      } else if (ts > existing.latestTs) {
        existing.latestTs = ts;
      }
    }

    const points: PriceHistoryPoint[] = Array.from(byDay.entries())
      .sort(([a], [b]) => (a < b ? -1 : 1))
      .map(([, v]) => ({
        checkedAt: v.row.checked_at,
        displayPrice: v.row.display_price,
        realMinPrice: v.row.real_min_price,
      }));

    const prices = points.map((p) => p.displayPrice);
    return {
      points,
      min: Math.min(...prices),
      max: Math.max(...prices),
      current: points[points.length - 1]?.displayPrice ?? null,
      currency: rows[0]?.currency ?? "PLN",
    };
  });
